import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Settings } from '../../lib/app.constant';
import { BehaviorSubject, forkJoin, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AdministrationService {
  private privilegeListSource = new BehaviorSubject<any>([]);
  privilegeList$ = this.privilegeListSource.asObservable();
  public endPointUrl = environment.baseApiUrl;

  constructor(private http: HttpClient) { }

  setPrivilegeList(privilegeList: any[]) {
    this.privilegeListSource.next(privilegeList);
  }

  getAllUser(body: {}, isExport: boolean = false) {
    if (!isExport) {
      return this.http.post(environment.baseApiUrl + 'user/v1/list', body);
    }
    else {
      return this.http.post(environment.baseApiUrl + 'user/v1/list', body, { observe: 'response', responseType: 'blob' });
    }
  }
  addUser(body: {}) {
    return this.http.post(environment.baseApiUrl + 'user/v1/create-user', body);
  }
  updateUser(body: {}, id: string) {
    return this.http.put(environment.baseApiUrl + 'user/v1/users/update/' + id, body);
  }
  getUser(body: {}, id: string) {
    return this.http.get(environment.baseApiUrl + 'user/v1/detail/' + id, body);
  }
  patchUser(body: {}, id: string) {
    return this.http.patch(environment.baseApiUrl + 'user/v1/users/superuser/' + id, body);
  }
  changeSuperUser(body: {}, id: any) {
    return this.http.put(environment.baseApiUrl + 'user/v1/superuser/modify/' + id, body);
  }
  deleteUser(id: string) {
    return this.http.delete(environment.baseApiUrl + 'user/v1/users/delete/' + id);
  }

  getApplicationRoleDataAccess(id: any) {
    return this.http.get(environment.baseApiUrl + 'user/v1/user/profile/access/' + id)
  }
  getRolePrivilegeView(id: any) {
    return this.http.get(environment.baseApiUrl + 'acl/roles/' + id)
  }

  getColumnSettingsList(body: any = {}, isExport: boolean = false) {
    if (!isExport) {
      return this.http.post(environment.baseApiUrl + 'config/v1/bulk-configurations/list', body);
    }
    else {
      return this.http.post(environment.baseApiUrl + 'config/v1/bulk-configurations/list', body, { observe: 'response', responseType: 'blob' });
    }
  }

  getUserColumnSettingsList(module_id: any, application_id: any) {
    return this.http.get(environment.baseApiUrl + 'config/v1/user-column-settings/list/module/' + module_id + '/application/' + application_id);
  }

  postUserColumnSettings(body: any = {}) {
    // Column objects carry the px-converted width at runtime; persist the
    // original character count instead.
    if (Array.isArray(body?.payload)) {
      body = {
        ...body,
        payload: body.payload.map((col: any) =>
          this.pxToCharsCache.has(col?.width)
            ? { ...col, width: this.pxToCharsCache.get(col.width) }
            : col
        )
      };
    }
    return this.http.post(environment.baseApiUrl + 'config/v1/user-column-settings', body);
  }

  //  *---------------Masterdata Document Type-----------------*
  getDocumentType(body: {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `document/v1/document-type/list`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post(environment.baseApiUrl + `document/v1/document-type/list`, body);
    }
  }
  postDocumentType(body: {}) {
    return this.http.post(environment.baseApiUrl + 'document/v1/document-type', body);
  }
  deleteDocumentType(id: any) {
    return this.http.delete(environment.baseApiUrl + `document/v1/document-type/${id}`);
  }
  updateDocumentType(body: {}, id: any) {
    return this.http.put(environment.baseApiUrl + `document/v1/document-type/${id}`, body);
  }

  //  *---------------Masterdata Document Field-----------------*
  getDocumentFieldConfig(body: any = {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `document/v1/document-field/list`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `document/v1/document-field/list`, body);
    }
  }
  postDocumentField(body: {}) {
    return this.http.post(environment.baseApiUrl + 'document/v1/document-field', body);
  }
  deleteDocumentField(id: any) {
    return this.http.delete(environment.baseApiUrl + `document/v1/document-field/${id}`);
  }
  updateDocumentField(body: {}, id: any) {
    return this.http.put(environment.baseApiUrl + `document/v1/document-field/${id}`, body);
  }

  postDocumentFieldLibrary(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'document/v1/document-field/library', body);
  }

  getDocumentFieldLibrary(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'document/v1/document-field/library/list', body);
  }

  getRoleList(body: {}, isExport: boolean = false) {
    if (!isExport) {
      return this.http.post(environment.baseApiUrl + 'acl/role/list', body);
    }
    else {
      return this.http.post(environment.baseApiUrl + 'acl/role/list', body, { observe: 'response', responseType: 'blob' });
    }
  }

  submitColumnSetting(body: {}) {
    return this.http.post(environment.baseApiUrl + 'config/v1/bulk-configurations', body);
  }

  addRole(body: {}) {
    return this.http.post(environment.baseApiUrl + 'acl/roles/create', body);
  }

  getRole(id: any) {
    return this.http.get<any>(environment.baseApiUrl + 'acl/roles/' + id);
  }

  updateRole(body: {}, id: string) {
    return this.http.put(environment.baseApiUrl + 'acl/roles/' + id, body);
  }

  deleteRole(id: any) {
    return this.http.delete(environment.baseApiUrl + 'acl/roles/' + id);
  }

  getPrivilegelist(body: any = {}) {
    return this.http.post(environment.baseApiUrl + "acl/privilege/list", body);
  }

  //  *---------------Administration Application-----------------*
  getApplicationList(body: any = {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `user/v1/application/list`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `user/v1/application/list`, body);
    }
  }
  getApplicationListShortInfo() {
    return this.http.get<any>(environment.baseApiUrl + `user/v1/application/short/info`);
  }
  getApplicationLogo(application_id: any) {
    return this.http.get(environment.baseApiUrl + 'user/v1/application/logo/' + application_id);
  }
  generateAppApiToken(body: {}) {
    return this.http.post(environment.baseApiUrl + 'user/v1/token/generate', body);
  }
  reGenerateAppApiToken(body: any = {}, token_id: any) {
    return this.http.post(environment.baseApiUrl + 'user/v1/token/reset/' + token_id, body);
  }
  getAppApiToken(id: any) {
    return this.http.get(environment.baseApiUrl + 'user/v1/tokens/' + id);
  }
  postApplication(body: {}) {
    return this.http.post(environment.baseApiUrl + 'user/v1/application/register', body);
  }
  deleteApplication(id: any) {
    return this.http.delete(environment.baseApiUrl + `user/v1/application/${id}`);
  }
  updateApplication(body: {}, id: any) {
    return this.http.put(environment.baseApiUrl + `user/v1/application/modify/${id}`, body);
  }

  getApplicationPrivilegeList(body: any = {}) {
    return this.http.post<any>(environment.baseApiUrl + `acl/privilege/list`, body);
  }

  getApplicationPrivileges() {
    return this.http.get<any>(environment.baseApiUrl + `acl/privilege`);
  }

  getUserDocumentTypeList(body: any = {}) {
    return this.http.post<any>(environment.baseApiUrl + `document/v1/user/document-type/list`, body);
  }
  getUserBusinessUnitList(body: any = {}) {
    return this.http.post<any>(environment.baseApiUrl + `document/v1/user/business-unit/list`, body);
  }

  //  *---------------Administration Announcement-----------------*
  getAnnouncementList(body: any = {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `notification/v1/announcement/admin/list`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `notification/v1/announcement/admin/list`, body);
    }
  }
  postAnnouncement(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'notification/v1/announcement', body);
  }
  deleteAnnouncement(id: any) {
    return this.http.delete(environment.baseApiUrl + `notification/v1/announcement/${id}`);
  }

  //  *---------------Administration RetentionPolicy-----------------*
  getRetentionPolicyList(body: any = {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `admin/v1/retention/policy/list`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `admin/v1/retention/policy/list`, body);
    }
  }
  postRetentionPolicy(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'admin/v1/retention/policy', body);
  }
  deleteRetentionPolicy(id: any) {
    return this.http.delete(environment.baseApiUrl + `admin/v1/retention/policy/${id}`);
  }
  updateRetentionPolicy(body: any = {}, id: any) {
    return this.http.put(environment.baseApiUrl + `admin/v1/retention/policy/${id}`, body);
  }


  getEmailLogs(body: any = {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `document/v1/email/log`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `document/v1/email/log`, body);
    }
  }

  getDataChangeLogs(body: any = {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `notification/v1/history/data_change/log`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `notification/v1/history/data_change/log`, body);
    }
  }

  //  *---------------Administration RetentionPolicy-----------------*
  getAppDocConfigList(body: any = {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `document/v1/application/document/type/list`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `document/v1/application/document/type/list`, body);
    }
  }
  postAppDocConfig(body: {}) {
    return this.http.post(environment.baseApiUrl + 'document/v1/application/document/type', body);
  }
  deleteAppDocConfig(id: any) {
    return this.http.delete(environment.baseApiUrl + `document/v1/application/document/type/${id}`);
  }
  updateAppDocConfig(body: any = {}, id: any) {
    return this.http.put(environment.baseApiUrl + `document/v1/application/document/type/${id}`, body);
  }

  //  *---------------Masterdata File Type-----------------*
  getFileTypeList(body: any = {}, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `master/v1/file-type/list`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `master/v1/file-type/list`, body);
    }
  }
  postFileType(body: {}) {
    return this.http.post(environment.baseApiUrl + 'master/v1/file-type', body);
  }
  deleteFileType(id: any) {
    return this.http.delete(environment.baseApiUrl + `master/v1/file-type/${id}`);
  }
  updateFileType(body: any = {}, id: any) {
    return this.http.put(environment.baseApiUrl + `master/v1/file-type/${id}`, body);
  }

  saveFilterTemplate(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'document/v1/user-preference', body);
  }

  getFilterTemplateList(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'document/v1/user-preference/filter-template/list', body);
  }
  deleteFilterTemplateById(module_id: any) {
    return this.http.delete(environment.baseApiUrl + 'document/v1/user-preference/destroy/' + module_id);
  }
  preparePayload(payload: any): any {
    const filteredPayload = { ...payload };
    Object.keys(filteredPayload).forEach(key => {
      const startDateKey = `${key}_start_date`;
      const endDateKey = `${key}_end_date`;
      if (filteredPayload[startDateKey]) {
        if (filteredPayload[endDateKey] == null) { // Check if end_date is null or undefined
          filteredPayload[endDateKey] = filteredPayload[startDateKey];
          delete filteredPayload[key];
        } else if (filteredPayload[startDateKey] && filteredPayload[endDateKey]) {
          delete filteredPayload[key];
        }
      }
    });
    return filteredPayload;
  }

  initializeFilterModel(column_list: any[]) {
    let filterModel: any = {};
    column_list.forEach(col => {
      if (col.is_filterable) {
        if (col.filter_type === 'calendar') {
          filterModel[`${col.key}_start_date`] = null;
          filterModel[`${col.key}_end_date`] = null;
        }
        else if (col.filter_type === 'input') {
          if (col.key.endsWith('_by')) {
            filterModel[col.key] = null;
            filterModel[`${col.key}_name`] = null;
          }
          else if (col.key.endsWith('_to')) {
            filterModel[col.key] = null;
            filterModel[`${col.key}_name`] = null;
          }
          else {
            filterModel[col.key] = null;
          }
        }
        else {
          filterModel[col.key] = null;
        }
      }
    });
    return filterModel;
  }

  groupData(listData: any[]) {
    // Initialize groups
    const activeData = listData.filter(app => app.status);
    const inactiveData = listData.filter(app => !app.status);

    // Create grouped list with headers
    let groupedList = [];

    if (activeData.length > 0) {
      groupedList.push({
        label: 'Active',
        items: activeData
      });
    }

    if (inactiveData.length > 0) {
      groupedList.push({
        label: 'Inactive',
        items: inactiveData
      });
    }

    return groupedList;
  }

  //  *---------------Upload File-----------------*
  uploadFile(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'edi/v1/file/upload', body);
  }
  // *---------------Upload File for Map Library-----------------*
  uploadFileForCustom(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'master-data/custom-file-definitions/upload-file', body);
  }
  uploadFileForXsd(body: any = {}) {
    return this.http.post(environment.baseApiUrl + 'master-data/file/definition/details/xsd-upload', body);
  }

  charToPx(text: string, fontSize: string = '12px', fontFamily: string = 'DFDS-Regular'): number {
    const span = document.createElement('span');
    span.style.position = 'absolute';
    span.style.visibility = 'hidden';
    span.style.font = `${fontSize} ${fontFamily}`;
    span.textContent = text;
    document.body.appendChild(span);
    const width = span.offsetWidth;
    document.body.removeChild(span);
    return width;
  };

  private columnWidthCache = new Map<number, number>();
  private pxToCharsCache = new Map<number, number>();

  // Column-setting widths are stored as character counts; tables need px.
  getColumnWidthPx(width: number | null | undefined): number | null {
    const chars = Number(width);
    if (!chars || chars <= 0) {
      return null;
    }
    if (this.columnWidthCache.has(chars)) {
      return this.columnWidthCache.get(chars)!;
    }
    const px = this.charToPx('S'.repeat(chars));
    this.columnWidthCache.set(chars, px);
    this.pxToCharsCache.set(px, chars);
    return px;
  }

  /**
   * Converts the privileges array into a formatted structure.
   * @param privileges The array of privileges.
   * @returns The formatted privileges structure.
   */
  convertPrivileges(privileges: any[]): any[] {
    const formattedPrivileges: any = {};

    privileges.forEach((privilege) => {
      const moduleId = Number.parseInt(privilege.module_id, 10);
      const moduleName = Settings.MasterModule[privilege.module_id];

      // If the module ID does not exist in the formattedPrivileges object,
      // create a new entry for it.
      if (!formattedPrivileges[moduleId]) {
        formattedPrivileges[moduleId] = {
          label: moduleName?.replaceAll('_', ' ').replaceAll(/\b\w/g, (c: any) => c.toUpperCase()),
          id: moduleId,
          items: [],
        };
      }

      // Add the privilege to the corresponding module entry.
      formattedPrivileges[moduleId].items.push({
        id: moduleId,
        label: privilege.privilege_name?.replaceAll('_', ' ').replaceAll(/\b\w/g, (c: any) => c.toUpperCase()),
        value: privilege.privilege_name,
      });
    });
    // Return the formatted privileges as an array.
    return Object.values(formattedPrivileges);
  }

  // Logger Service Function
  getLoggerList(body: any, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `edi/v1/doc/tracker`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post(environment.baseApiUrl + `edi/v1/doc/tracker`, body)
    }

  }
  getDocProcessingLog(body: any) {
    return this.http.post(environment.baseApiUrl + `logger/doc/processing/log`, body)
  }
  explainDocError(body: { document_id: number; log_id: number }) {
    return this.http.post(environment.baseApiUrl + `logger/doc/error/explain`, body)
  }
  // For Column Settings
  // Static per module (backed by in-memory MODULE_KEYS, Cache-Control 1h):
  // memoize so every tab switch doesn't pay 2 extra RTTs.
  private keyListCache = new Map<any, any>();
  private urlListCache = new Map<number, any[]>();
  private filterUrlCache = new Map<number, any[]>();

  getKeyList(id: any) {
    if (this.keyListCache.has(id)) {
      return of(this.keyListCache.get(id));
    }
    return this.http.get(environment.baseApiUrl + 'config/api/module/' + id + '/key-dropdown').pipe(
      tap((res) => this.keyListCache.set(id, res))
    );
  }
  getUrlList(id: number) {
    if (this.urlListCache.has(id)) {
      return of(this.urlListCache.get(id));
    }
    return this.http.get<any[]>(
      environment.baseApiUrl + 'config/module/' + id + '/dropdown-options'
    ).pipe(
      tap((res) => this.urlListCache.set(id, res ?? []))
    );
  }

  clearColumnDropdownCache(id?: number) {
    if (id === undefined) {
      this.keyListCache.clear();
      this.urlListCache.clear();
      this.filterUrlCache.clear();
    } else {
      this.keyListCache.delete(id);
      this.urlListCache.delete(id);
      this.filterUrlCache.delete(id);
    }
  }

  getFilterUrlList(id: number) {
    if (this.filterUrlCache.has(id)) {
      return of(this.filterUrlCache.get(id) ?? []);
    }
    return this.http.get<any[]>(
      environment.baseApiUrl + 'config/v1/column-keys/' + id
    ).pipe(
      tap((res) => this.filterUrlCache.set(id, res ?? []))
    );
  }

  /** Batched fan-out: one concurrent burst for distinct ids, cached per id. */
  getFilterUrlLists(ids: number[]) {
    const distinct = [...new Set((ids || []).filter((v) => v !== null && v !== undefined))];
    if (!distinct.length) {
      return of([] as any[][]);
    }
    const cached = new Map<number, any[]>();
    const missing: number[] = [];
    distinct.forEach((id) => {
      if (this.filterUrlCache.has(id)) {
        cached.set(id, this.filterUrlCache.get(id) ?? []);
      } else {
        missing.push(id);
      }
    });
    if (!missing.length) {
      return of(distinct.map((id) => cached.get(id) ?? []));
    }
    const calls = missing.map((id) =>
      this.http.get<any[]>(environment.baseApiUrl + 'config/v1/column-keys/' + id).pipe(
        tap((res) => this.filterUrlCache.set(id, res ?? []))
      )
    );
    return forkJoin(calls).pipe(
      map((fetched) => {
        missing.forEach((id, i) => cached.set(id, fetched[i] ?? []));
        return distinct.map((id) => cached.get(id) ?? []);
      })
    );
  }
  getJson(url: string): Observable<any> {

    return this.http.get(url, { responseType: 'text' }).pipe(

      map((data) => JSON.parse(data))

    );

  }
  // for users admin
  public getProfileDetails() {
    return this.http.get(this.endPointUrl + "user/v1/profile")
  }
  // for Connectivity
  getConnectivityList(body: any, is_export: boolean = false) {
    if (is_export) {
      return this.http.post(environment.baseApiUrl + `connectivity/trading/partner/connectivity/setup/list`, body, { observe: 'response', responseType: 'blob' });
    } else {
      return this.http.post<any>(environment.baseApiUrl + `connectivity/trading/partner/connectivity/setup/list`, body);
    }
  }
  addConnectivityForm(body: {}) {
    return this.http.post(environment.baseApiUrl + 'connectivity/trading/partner/connectivity/setup', body);
  }
  deleteConnectivity(id: any) {
    return this.http.delete(environment.baseApiUrl + `connectivity/trading/partner/connectivity/setup/${id}`);
  }
  getConnectivity(id: number) {
    return this.http.get(environment.baseApiUrl + `connectivity/trading/partner/connectivity/setup/${id}`);
  }
  updateConnectivity(body: {}, id: any) {
    return this.http.put(environment.baseApiUrl + `connectivity/trading/partner/connectivity/setup/${id}`, body);
  }
  addAPIConnectivity(body: {}) {
    return this.http.post(environment.baseApiUrl + 'connectivity/api/', body);
  }
  addSMTPConnectivity(body: {}) {
    return this.http.post(environment.baseApiUrl + 'connectivity/smtp/', body);
  }
  addSFTPConnectivity(body: {}) {
    return this.http.post(environment.baseApiUrl + 'connectivity/sftp/', body);
  }
  addFSAConnectivity(body: {}) {
    return this.http.post(environment.baseApiUrl + 'connectivity/fsa/', body);
  }
  addFTPConnectivity(body: {}) {
    return this.http.post(environment.baseApiUrl + 'connectivity/ftp/', body);
  }
  addAS2Connectivity(body: {}) {
    return this.http.post(environment.baseApiUrl + 'connectivity/as2', body);
  }
  getSMTPConnectivityDetails(id: number) {
    return this.http.get(environment.baseApiUrl + `connectivity/smtp/get/${id}`);
  }
  getSFTPConnectivityDetails(id: number) {
    return this.http.get(environment.baseApiUrl + `connectivity/sftp/get/${id}`);
  }
  getAPIConnectivityDetails(id: number) {
    return this.http.get(environment.baseApiUrl + `connectivity/api/get/${id}`);
  }
  getFSAConnectivityDetails(id: number) {
    return this.http.get(environment.baseApiUrl + 'connectivity/fsa/' + id);
  }
  getFTPConnectivityDetails(id: number) {
    return this.http.get(environment.baseApiUrl + 'connectivity/ftp/' + id);
  }
  getAS2ConnectivityDetails(id: number) {
    return this.http.get(environment.baseApiUrl + 'connectivity/as2/' + id);
  }
  updateSFTPConnectivity(id: string, body: {}) {
    return this.http.put(environment.baseApiUrl + `connectivity/sftp/${id}`, body);
  }
  updateSMTPConnectivity(id: string, body: {}) {
    return this.http.put(environment.baseApiUrl + `connectivity/smtp/${id}`, body);
  }
  updateAPIConnectivity(id: string, body: {}) {
    return this.http.put(environment.baseApiUrl + `connectivity/api/${id}`, body);
  }
  updateFSAConnectivity(id: string, body: {}) {
    return this.http.put(environment.baseApiUrl + `connectivity/fsa/${id}`, body);
  }
  updateFTPConnectivity(id: string, body: {}) {
    return this.http.put(environment.baseApiUrl + `connectivity/ftp/${id}`, body);
  }
  updateAS2Connectivity(id: string, body: {}) {
    return this.http.put(environment.baseApiUrl + `connectivity/as2/${id}`, body);
  }
  deleteSMTPConnectivityDetails(id: number) {
    return this.http.delete(environment.baseApiUrl + `connectivity/smtp/${id}`);
  }
  deleteSFTPConnectivityDetails(id: number) {
    return this.http.delete(environment.baseApiUrl + `connectivity/sftp/${id}`);
  }
  deleteAPIConnectivityDetails(id: number) {
    return this.http.delete(environment.baseApiUrl + `connectivity/api/${id}`);
  }
  deleteAS2ConnectivityDetails(id: number) {
    return this.http.delete(environment.baseApiUrl + `connectivity/as2/${id}`);
  }
  deleteFSAConnectivityDetails(id: number) {
    return this.http.delete(environment.baseApiUrl + `connectivity/fsa/${id}`);
  }
  deleteFTPConnectivityDetails(id: number) {
    return this.http.delete(environment.baseApiUrl + `connectivity/ftp/${id}`);
  }
  testConnectivity(setupId: string) {
    return this.http.post<any>(
      environment.baseApiUrl + 'connectivity/trading/partner/connectivity/test',
      { connectivity_setup_id: setupId }
    );
  }
  registerInitial(body: any) {
    const url = environment.baseApiUrl + 'user/v1/user/register';
    return this.http.post(url, body);
  }
  // CUSTOMER MANAGEMENT
  getCustomerList(body: {}, isExport: boolean = false) {
    if (!isExport) {
      return this.http.post(environment.baseApiUrl + 'master-data/customer/list', body);
    }
    else {
      return this.http.post(environment.baseApiUrl + 'master-data/customer/list', body, { observe: 'response', responseType: 'blob' });
    }
  }
  addCustomer(body: {}) {
    return this.http.post(environment.baseApiUrl + 'master-data/customer', body);
  }
  getCustomer(id: any) {
    return this.http.get(environment.baseApiUrl + 'master-data/customer/' + id)
  }
  updateCustomer(body: {}, id: string) {
    return this.http.put(environment.baseApiUrl + `master-data/customer/${id}`, body);
  }
  deleteCustomer(id: number) {
    return this.http.delete(environment.baseApiUrl + `master-data/customer/${id}`);
  }

  // Regex Management
  getRegexList(body: {}, isExport: boolean = false) {
    if (!isExport) {
      return this.http.post(environment.baseApiUrl + 'master-data/regex_format/list', body);
    }
    else {
      return this.http.post(environment.baseApiUrl + 'master-data/regex_format/list', body, { observe: 'response', responseType: 'blob' });
    }
  }

  addRegex(body: {}) {
    return this.http.post(environment.baseApiUrl + 'master-data/regex_format/create', body);
  }
  getRegex(id: string) {
    return this.http.get(environment.baseApiUrl + 'master-data/regex_format/' + id)
  }
  updateRegex(body: {}, id: string) {
    return this.http.put(environment.baseApiUrl + `master-data/regex_format/${id}`, body);
  }
  deleteRegex(id: string) {
    return this.http.delete(environment.baseApiUrl + `master-data/regex_format/${id}`);
  }
  // send Email Doc Tracker
  sendEmailDocTracker(body: {}) {
    return this.http.post(environment.baseApiUrl + 'edi/v1/doc/send-email', body);
  }
  sendFileAttachedDocTracker(body: {}) {
    return this.http.post(environment.baseApiUrl + 'edi/v1/doc/attachment', body);
  }
  sendActionRemarkDocTracker(body: {}) {
    return this.http.post(environment.baseApiUrl + 'edi/v1/doc/action', body);
  }
  // USER ACTIVITY LOGS
  getUserActivityList(body: {}, isExport: boolean = false) {
    if (!isExport) {
      return this.http.post(environment.baseApiUrl + 'notification/admin/user-activity/list', body);
    }
    else {
      return this.http.post(environment.baseApiUrl + 'notification/admin/user-activity/list', body, { observe: 'response', responseType: 'blob' });
    }
  }
  getUserActivityDetail(id: number | string) {
    return this.http.get(environment.baseApiUrl + 'notification/admin/user-activity/' + id);
  }
  // Email Template
  getEmailTemplateList(body: {}, isExport: boolean = false) {
    if (!isExport) {
      return this.http.post(environment.baseApiUrl + 'email-config/email_template/manual/list', body);
    }
    else {
      return this.http.post(environment.baseApiUrl + 'email-config/email_template/manual/list', body, { observe: 'response', responseType: 'blob' });
    }
  }
  addEmail(body: {}) {
    return this.http.post(environment.baseApiUrl + 'email-config/v1/email_template/manual/create', body);
  }
  getEmail(id: any) {
    return this.http.get(environment.baseApiUrl + `/email-config/v1/email_template/manual/${id}`);
  }
  updateEmail(body: {}, id: string) {
    return this.http.patch(environment.baseApiUrl + `email-config/v1/email_template/manual/${id}`, body);
  }
  deleteEmail(id: number | string) {
    return this.http.delete(environment.baseApiUrl + `email-config/v1/email_template/manual/${id}`);
  }
}
